/**
 * ReadyStep.tsx — the completion step of the VER-128 Create Programme preview: a summary of
 * what was chosen and the "what do you want to do first" action grid.
 *
 * "Rehearse a scenario" is included as a selectable card because the canvas shows it, but it
 * is not wired to anything — the AI-simulated rehearsal feature does not exist yet (see the
 * "Out of scope" list on VER-128).
 */
import React from "react";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { NEXT_ACTIONS, SESSION_SCHEDULE_SUMMARY, SUMMARY_COLLECTION_NAME } from "./createProgramme.mock";
import { NEXT_ACTION_ICONS } from "./createProgramme.icons";
import { NextActionKey } from "./createProgramme.types";
import { SelectableOptionCard } from "./SelectableOptionCard";

type ReadyStepProps = {
    populationLabel: string;
    formatTitle: string;
    nextAction: NextActionKey;
    onNextActionChange: (value: NextActionKey) => void;
};

const SummaryRow: React.FC<{ label: string; value: string; emphasise?: boolean; last?: boolean }> = ({
    label,
    value,
    emphasise,
    last,
}) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: 2.5, py: 1.75, borderBottom: last ? "none" : "1px solid", borderColor: "borderShade.main" }}
    >
        <Typography variant="overline" color="text.secondary">
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={700}
            color={emphasise ? "primary.main" : "secondary.main"}
            textAlign="right"
        >
            {value}
        </Typography>
    </Stack>
);

export const ReadyStep: React.FC<ReadyStepProps> = ({
    populationLabel,
    formatTitle,
    nextAction,
    onNextActionChange,
}) => {
    return (
        <Stack spacing={3}>
            <Stack spacing={0.5}>
                <Typography variant="overline" color="primary.main">
                    Ready
                </Typography>
                <Typography variant="h3" component="h2" color="secondary.main">
                    Your programme is ready
                </Typography>
            </Stack>

            <Stack spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                <Box
                    sx={{
                        width: 62,
                        height: 62,
                        borderRadius: 2,
                        bgcolor: "primary.light",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CheckIcon sx={{ fontSize: 34, color: "primary.main" }} />
                </Box>
                <Typography variant="h4" color="secondary.main">
                    Your programme is ready
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: "46ch" }}>
                    Everything is set up. Session 1 is waiting — start with the baseline assessment, or prepare your
                    discussion guide.
                </Typography>
            </Stack>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                <SummaryRow label="Population" value={populationLabel} emphasise />
                <SummaryRow label="Format" value={formatTitle} />
                <SummaryRow label="Story collection" value={SUMMARY_COLLECTION_NAME} />
                <SummaryRow label="Sessions" value={SESSION_SCHEDULE_SUMMARY} />
                <SummaryRow label="First session" value="Not scheduled" last />
            </Paper>

            <Stack spacing={1.5}>
                <Typography variant="subtitle1" color="secondary.main">
                    What do you want to do first?
                </Typography>
                <Grid container spacing={1.75}>
                    {NEXT_ACTIONS.map((action) => (
                        <Grid item xs={12} sm={6} key={action.key}>
                            <SelectableOptionCard
                                title={action.title}
                                body={action.body}
                                icon={NEXT_ACTION_ICONS[action.key]}
                                selected={nextAction === action.key}
                                onClick={() => onNextActionChange(action.key)}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        </Stack>
    );
};
